import { useEffect, useRef, useState } from 'react'
import type mapboxgl from 'mapbox-gl'
import { MAPBOX_TOKEN } from '../lib/geocode'

interface EventLocationMapProps {
  lat: number
  lng: number
  label?: string
  height?: number
}

function useMapInstance(
  containerRef: React.RefObject<HTMLDivElement | null>,
  lat: number,
  lng: number,
  interactive: boolean,
  enabled: boolean,
) {
  const mapRef = useRef<mapboxgl.Map | null>(null)
  const markerRef = useRef<mapboxgl.Marker | null>(null)
  const useMapbox = Boolean(MAPBOX_TOKEN) && import.meta.env.MODE !== 'test'

  useEffect(() => {
    if (!enabled || !containerRef.current || !useMapbox) return
    let cancelled = false

    void import('mapbox-gl').then((mod) => {
      if (cancelled || !containerRef.current) return
      const mb = mod.default
      mb.accessToken = MAPBOX_TOKEN as string

      const map = new mb.Map({
        container: containerRef.current,
        style: 'mapbox://styles/mapbox/streets-v12',
        center: [lng, lat],
        zoom: 14,
        interactive,
        attributionControl: false,
      })

      mapRef.current = map

      map.on('load', () => {
        if (cancelled) return
        map.resize()
        markerRef.current = new mb.Marker({ color: '#3B4CCA' })
          .setLngLat([lng, lat])
          .addTo(map)
      })
    })

    return () => {
      cancelled = true
      markerRef.current?.remove()
      markerRef.current = null
      mapRef.current?.remove()
      mapRef.current = null
    }
  }, [enabled, useMapbox, lat, lng, interactive, containerRef])

  return useMapbox
}

const BTN =
  'w-8 h-8 rounded-lg bg-white/90 backdrop-blur border border-black/10 flex items-center justify-center cursor-pointer text-ink hover:bg-white transition-colors shadow-sm'

export function EventLocationMap({ lat, lng, label, height = 280 }: EventLocationMapProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const fsContainerRef = useRef<HTMLDivElement>(null)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const useMapbox = Boolean(MAPBOX_TOKEN) && import.meta.env.MODE !== 'test'

  useMapInstance(containerRef, lat, lng, false, true)
  useMapInstance(fsContainerRef, lat, lng, true, isFullscreen)

  const gmapsUrl = `https://www.google.com/maps?q=${lat},${lng}`
  const osmPad = 0.012
  const osmBbox = `${lng - osmPad},${lat - osmPad},${lng + osmPad},${lat + osmPad}`
  const osmSrc = `https://www.openstreetmap.org/export/embed.html?bbox=${encodeURIComponent(osmBbox)}&layer=mapnik&marker=${lat}%2C${lng}`

  return (
    <>
      <div style={{ position: 'relative', height, borderRadius: 14, overflow: 'hidden' }}>
        {useMapbox ? (
          <div ref={containerRef} style={{ width: '100%', height: '100%' }} />
        ) : (
          <iframe
            title={label ? `Map showing ${label}` : 'Event location map'}
            src={osmSrc}
            style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        )}

        {/* Overlay buttons — top-right */}
        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <button
            type="button"
            onClick={() => setIsFullscreen(true)}
            className={BTN}
            aria-label="Expand map"
            title="Expand map"
          >
            <ExpandIcon />
          </button>
          <a
            href={gmapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={BTN}
            aria-label="Open in Google Maps"
            title="Open in Google Maps"
          >
            <GoogleMapsIcon />
          </a>
        </div>
      </div>

      {/* Full-screen modal */}
      {isFullscreen && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.82)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={(e) => { if (e.target === e.currentTarget) setIsFullscreen(false) }}
        >
          <div style={{ position: 'relative', width: '90vw', height: '80vh', borderRadius: 16, overflow: 'hidden' }}>
            {useMapbox ? (
              <div ref={fsContainerRef} style={{ width: '100%', height: '100%' }} />
            ) : (
              <iframe
                title={label ? `Map showing ${label}` : 'Event location map'}
                src={osmSrc}
                style={{ width: '100%', height: '100%', border: 0, display: 'block' }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            )}

            {/* Fullscreen overlay buttons */}
            <div style={{ position: 'absolute', top: 12, right: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                onClick={() => setIsFullscreen(false)}
                className={BTN}
                aria-label="Close map"
                title="Close map"
              >
                <CloseIcon />
              </button>
              <a
                href={gmapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={BTN}
                aria-label="Open in Google Maps"
                title="Open in Google Maps"
              >
                <GoogleMapsIcon />
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ─── Inline SVG icons ─────────────────────────────────────────────────────────

function ExpandIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  )
}

function CloseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function GoogleMapsIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#3B4CCA" stroke="none" />
      <circle cx="12" cy="9" r="2.5" fill="white" stroke="none" />
    </svg>
  )
}
